/**
 * Date field formatter
 * Handles conversion between Date objects and ISO strings for database storage
 */

export default {
  type: "Date",

  /**
   * preFormat - Convert ISO strings from database to Date objects for the editor
   */
  preFormat: async (fieldValues, Type) => {
    if (!Type?.fields) return fieldValues

    for (const field of Type.fields) {
      // Check if field uses a Date editor
      const isDateField = field.fieldEditor === "DateEditor" ||
                          field.fieldEditor === "DateRangeEditor"

      if (isDateField && fieldValues[field.id]) {
        const value = fieldValues[field.id]

        // Handle single date values
        if (typeof value === "string" && value !== "") {
          try {
            fieldValues[field.id] = new Date(value)
          } catch (error) {
            console.error(`Failed to parse date for field ${field.id}:`, error)
          }
        }
        // Handle date range objects {startDate, endDate}
        else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          if (value.startDate && typeof value.startDate === "string") {
            try {
              value.startDate = new Date(value.startDate)
            } catch (error) {
              console.error(`Failed to parse startDate for field ${field.id}:`, error)
            }
          }
          if (value.endDate && typeof value.endDate === "string") {
            try {
              value.endDate = new Date(value.endDate)
            } catch (error) {
              console.error(`Failed to parse endDate for field ${field.id}:`, error)
            }
          }
        }
        // Handle array of dates (for multiple dates)
        else if (Array.isArray(value)) {
          fieldValues[field.id] = value.map(item => {
            if (typeof item === "string" && item !== "") {
              try {
                return new Date(item)
              } catch (error) {
                console.error(`Failed to parse array date for field ${field.id}:`, error)
                return item
              }
            } else if (typeof item === "object" && item !== null) {
              // Handle date range in array
              if (item.startDate && typeof item.startDate === "string") {
                try {
                  item.startDate = new Date(item.startDate)
                } catch (error) {
                  console.error(`Failed to parse array startDate for field ${field.id}:`, error)
                }
              }
              if (item.endDate && typeof item.endDate === "string") {
                try {
                  item.endDate = new Date(item.endDate)
                } catch (error) {
                  console.error(`Failed to parse array endDate for field ${field.id}:`, error)
                }
              }
              return item
            }
            return item
          })
        }
      }
    }

    return fieldValues
  },

  /**
   * postFormat - Convert Date objects to ISO strings for database storage
   */
  postFormat: async (fieldValues, Type) => {
    if (!Type?.fields) {
      return fieldValues
    }

    for (const field of Type.fields) {
      // Check if field uses a Date editor
      const isDateField = field.fieldEditor === "DateEditor" ||
                          field.fieldEditor === "DateRangeEditor"

      if (isDateField && fieldValues[field.id]) {
        const value = fieldValues[field.id]

        // Handle single Date objects
        if (value instanceof Date) {
          if (!isNaN(value.getTime())) {
            fieldValues[field.id] = value.toISOString()
          } else {
            delete fieldValues[field.id]
          }
        }
        // Handle date range objects {startDate, endDate}
        else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          if (value.startDate instanceof Date && !isNaN(value.startDate.getTime())) {
            value.startDate = value.startDate.toISOString()
          } else if (value.startDate) {
            delete value.startDate
          }

          if (value.endDate instanceof Date && !isNaN(value.endDate.getTime())) {
            value.endDate = value.endDate.toISOString()
          } else if (value.endDate) {
            delete value.endDate
          }

          // If object is now empty, remove the field
          if (Object.keys(value).length === 0) {
            delete fieldValues[field.id]
          }
        }
        // Handle array of dates
        else if (Array.isArray(value)) {
          fieldValues[field.id] = value.map(item => {
            if (item instanceof Date && !isNaN(item.getTime())) {
              return item.toISOString()
            } else if (typeof item === "object" && item !== null) {
              // Handle date range in array
              if (item.startDate instanceof Date && !isNaN(item.startDate.getTime())) {
                item.startDate = item.startDate.toISOString()
              }
              if (item.endDate instanceof Date && !isNaN(item.endDate.getTime())) {
                item.endDate = item.endDate.toISOString()
              }
              return item
            }
            return item
          }).filter(item => {
            // Filter out invalid entries
            if (item instanceof Date) return !isNaN(item.getTime())
            if (typeof item === "object" && item !== null) {
              return Object.keys(item).length > 0
            }
            return item != null
          })
        }
        // Handle invalid values
        else if (typeof value === "object" && value !== null) {
          // Check if it's an empty object
          if (Object.keys(value).length === 0) {
            delete fieldValues[field.id]
          }
        }
      }
    }

    return fieldValues
  }
}
